import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { money } from '../events/util';

// LOS COMPROBANTES EMITIDOS Y LOS QUE FALLARON.
//
// Un comprobante que no salió no puede quedar escondido: es una venta sin factura. Acá se
// ven los tres estados, con el motivo exacto de ARCA en los que fallaron, y un botón para
// reintentarlos cuando la causa está arreglada (el certificado asociado, la alícuota
// corregida). Mientras tanto la venta ya se cobró: la cola nunca frena una caja.
const ESTADOS = {
  emitido: { t: 'Emitido', c: '#2ecc71' },
  pendiente: { t: 'En cola', c: '#d4af37' },
  error: { t: 'Con error', c: 'var(--red-400)' },
  anulado: { t: 'Anulado', c: 'var(--ink-600)' },
};
const TIPOS = { 6: 'Factura B', 8: 'N. crédito B', 11: 'Factura C', 13: 'N. crédito C' };
const ORIGENES = { ticket: 'Venta online', tpv_sale: 'Caja TPV', prueba: 'Prueba' };

export default function ArcaComprobantes() {
  const api = useApi();
  const [d, setD] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    api.get('/api/arca/comprobantes' + (filtro ? `?estado=${filtro}` : ''))
      .then(setD).catch(e => setErr(e.message));
  }, [filtro]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  async function reintentar() {
    setBusy(true); setErr('');
    try { await api.post('/api/arca/comprobantes/reintentar', {}); load(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  if (!d) return null;
  const r = d.resumen || {};
  const conError = r.error?.n || 0;
  const fmt = (x) => (x ? new Date(x).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

  return (
    <div className="lb-card" style={{ padding: '24px 26px', marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>Comprobantes</div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-600)', marginTop: 4 }}>
            {(r.emitido?.n || 0)} emitidos · {(r.pendiente?.n || 0)} en cola{conError ? ` · ${conError} con error` : ''}
          </div>
        </div>
        {conError > 0 && (
          <button className="lb-btn lb-btn--white lb-btn--sm" disabled={busy} onClick={reintentar}>
            {busy ? 'Reintentando…' : `Reintentar los ${conError} con error`}
          </button>
        )}
      </div>

      {err && <div className="lb-err" style={{ marginTop: 12 }}>{err}</div>}

      <div style={{ display: 'flex', gap: 6, margin: '14px 0 10px', flexWrap: 'wrap' }}>
        {[['', 'Todos'], ['emitido', 'Emitidos'], ['pendiente', 'En cola'], ['error', 'Con error']].map(([k, t]) => (
          <button key={k} className={'lb-btn lb-btn--sm ' + (filtro === k ? 'lb-btn--white' : 'lb-btn--ghost')} onClick={() => setFiltro(k)}>{t}</button>
        ))}
      </div>

      <div className="tk-scroll-x">
        <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.1fr 1fr 1.2fr 0.9fr 1fr' }}>
          <span>Fecha</span><span>Origen</span><span>Comprobante</span><span>Importe</span><span>Estado</span>
        </div>
        {d.comprobantes.map(c => {
          const st = ESTADOS[c.estado] || ESTADOS.pendiente;
          return (
            <div key={c.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="lb-row" style={{ gridTemplateColumns: '1.1fr 1fr 1.2fr 0.9fr 1fr', borderBottom: 0, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--ink-700)' }}>{fmt(c.emitido_at || c.created_at)}</span>
                <span style={{ fontSize: 12, color: 'var(--ink-800)' }}>{ORIGENES[c.origen] || c.origen}</span>
                <span style={{ fontSize: 12.5, color: '#fff' }}>
                  {TIPOS[c.tipo] || `Tipo ${c.tipo}`}
                  {c.numero ? <span style={{ color: 'var(--ink-700)' }}> {String(c.punto_venta).padStart(4, '0')}-{String(c.numero).padStart(8, '0')}</span> : null}
                  {c.cae && <div style={{ fontSize: 10.5, color: 'var(--ink-600)', fontFamily: 'monospace' }}>CAE {c.cae}{c.cae_vto ? ` · vence ${new Date(c.cae_vto).toLocaleDateString('es-AR')}` : ''}</div>}
                </span>
                <span style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>{money(c.total)}</span>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: st.c }}>
                  {st.t}
                  {c.entorno === 'homologacion' && c.estado === 'emitido' && <span style={{ color: 'var(--ink-600)', fontWeight: 400 }}> · prueba</span>}
                </span>
              </div>
              {/* El motivo de ARCA, tal cual: es lo que dice qué hay que arreglar. */}
              {c.ultimo_error && c.estado !== 'emitido' && (
                <div style={{ fontSize: 11.5, color: 'var(--red-300)', padding: '0 0 10px', lineHeight: 1.5 }}>
                  {c.ultimo_error}{c.intentos > 0 ? ` (intento ${c.intentos})` : ''}
                </div>
              )}
            </div>
          );
        })}
        {d.comprobantes.length === 0 && (
          <div style={{ padding: '20px 0', textAlign: 'center', fontSize: 13, color: 'var(--ink-600)' }}>
            Todavía no se emitió ningún comprobante.
          </div>
        )}
      </div>
    </div>
  );
}
